import React, { useState, useEffect } from 'react';
import { Table, Button, Space, message, Input, Modal, Form, Select, Popconfirm } from 'antd';
import { PlusOutlined, SearchOutlined, TeamOutlined, ReloadOutlined, EditOutlined, DeleteOutlined, KeyOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

interface Member {
  id: number;
  user_name: string;
  member_name: string;
  member_phone: string;
  permission: number[];
  role_id: number;
  role_name: string;
  create_time: string;
}

interface Role {
  id: number;
  role_name: string;
  permission: number[];
}

export default function SystemMembers() {
  const [members, setMembers] = useState<Member[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<Member | null>(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [form] = Form.useForm();

  useEffect(() => {
    fetchMembers();
    fetchRoles();
  }, []);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch('/api/admin/system-members', {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        setMembers(resp.data || []);
      } else {
        message.error(resp?.error || '获取成员列表失败');
      }
    } catch (err) {
      message.error('获取成员列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch('/api/admin/roles', {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        setRoles(resp.data || []);
      }
    } catch (err) {
      console.error('获取角色列表失败:', err);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: Member) => {
    setEditingItem(record);
    form.setFieldsValue({
      user_name: record.user_name,
      member_name: record.member_name,
      member_phone: record.member_phone,
      role_id: record.role_id,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`/api/admin/system-members/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success('删除成功');
        fetchMembers();
      } else {
        message.error(resp?.error || '删除失败');
      }
    } catch (err) {
      message.error('删除失败');
    }
  };

  const handleResetPassword = async (id: number) => {
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`/api/admin/system-members/${id}/reset-password`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success('密码已重置为 123456');
      } else {
        message.error(resp?.error || '重置密码失败');
      }
    } catch (err) {
      message.error('重置密码失败');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const token = localStorage.getItem('admin_token');
      const url = editingItem 
        ? `/api/admin/system-members/${editingItem.id}`
        : '/api/admin/system-members';
      const method = editingItem ? 'PUT' : 'POST';

      const resp = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(values),
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success(editingItem ? '更新成功' : '创建成功');
        setModalVisible(false);
        fetchMembers();
      } else {
        message.error(resp?.error || '操作失败');
      }
    } catch (err) {
      // 表单验证失败
    }
  };

  // 过滤数据
  const filteredData = members.filter(m => 
    m.user_name?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    m.member_name?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    m.role_name?.toLowerCase().includes(searchKeyword.toLowerCase())
  );

  const columns: ColumnsType<Member> = [
    {
      title: '账号',
      dataIndex: 'user_name',
      key: 'user_name',
      width: 150,
      render: (text) => <span style={{ fontWeight: 500, color: '#1f2937' }}>{text}</span>
    },
    {
      title: '姓名',
      dataIndex: 'member_name',
      key: 'member_name',
      width: 120,
      render: (text) => text || '-'
    },
    {
      title: '电话',
      dataIndex: 'member_phone',
      key: 'member_phone',
      width: 140,
      render: (text) => text || '-'
    },
    {
      title: '角色',
      dataIndex: 'role_name',
      key: 'role_name',
      width: 150,
      render: (text) => (
        <span style={{ 
          background: '#eff6ff', 
          color: '#2563eb', 
          padding: '4px 12px', 
          borderRadius: 12,
          fontSize: 13
        }}>
          {text || '未分配'}
        </span>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      fixed: 'right' as const,
      render: (_, record) => (
        <div className="admin-table-actions">
          <Popconfirm
            title="重置密码"
            description="确定要将密码重置为 123456 吗？"
            onConfirm={() => handleResetPassword(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button 
              type="link" 
              size="small"
              icon={<KeyOutlined />}
              style={{ color: '#f59e0b' }}
            >
              重置密码
            </Button>
          </Popconfirm>
          <Button 
            type="link" 
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ color: '#3b82f6' }}
          >
            修改
          </Button>
          <Popconfirm
            title="删除成员"
            description="确定要删除这个成员吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button 
              type="link" 
              size="small"
              danger 
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div className="admin-page-title">
          <div className="admin-page-title-icon">
            <TeamOutlined />
          </div>
          <div>
            <h2>后台管理</h2>
            <p>管理后台账号</p>
          </div>
        </div>
        
        <Space size="middle">
          <Input
            placeholder="搜索成员..."
            prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            style={{ width: 220, borderRadius: 10 }}
            allowClear
          />
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchMembers}
            style={{ borderRadius: 10 }}
          >
            刷新
          </Button>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleAdd}
            style={{ 
              borderRadius: 10,
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              border: 'none',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
            }}
          >
            新增成员
          </Button>
        </Space>
      </div>
      
      <div className="admin-page-content">
        <div style={{ marginBottom: 16, color: '#6b7280', fontSize: 14 }}>
          共 <span style={{ color: '#3b82f6', fontWeight: 600 }}>{filteredData.length}</span> 条记录
        </div>
        
        <Table
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          rowKey="id"
          className="admin-table"
          scroll={{ x: 'max-content' }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total: number, range: [number, number]) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          }}
        />
      </div>
      
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <TeamOutlined style={{ color: 'white', fontSize: 16 }} />
            </div>
            <span>{editingItem ? '编辑成员' : '新增成员'}</span>
          </div>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleSubmit}
        okText="确定"
        cancelText="取消"
        width={500}
        destroyOnHidden
        centered
      >
        <Form form={form} layout="vertical" style={{ marginTop: 24 }}>
          <Form.Item
            name="user_name"
            label="账号"
            rules={[{ required: true, message: '请输入账号' }]}
          >
            <Input placeholder="请输入账号" />
          </Form.Item>
          
          {!editingItem && (
            <Form.Item
              name="password"
              label="密码"
              rules={[{ required: true, message: '请输入密码' }]}
            >
              <Input.Password placeholder="请输入密码" />
            </Form.Item>
          )}
          
          <Form.Item
            name="member_name"
            label="姓名"
          >
            <Input placeholder="请输入姓名" />
          </Form.Item>
          
          <Form.Item
            name="member_phone"
            label="电话"
          >
            <Input placeholder="请输入电话" />
          </Form.Item>
          
          <Form.Item
            name="role_id"
            label="角色"
            rules={[{ required: true, message: '请选择角色' }]}
          >
            <Select placeholder="请选择角色">
              {roles.map(role => (
                <Select.Option key={role.id} value={role.id}>
                  {role.role_name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
